import React, { useState, useCallback, useEffect, useMemo, Fragment  } from 'react';
import { useDropzone } from 'react-dropzone';
import { Dialog, Transition } from "@headlessui/react";
import { 
  Upload, 
  X, 
  Check, 
  Trash2, 
  Download, 
  Image as ImageIcon,
  AlertCircle,
  Loader2,
  Star,
  Pencil,
  Eye,
  CheckCircle2,
  Info
} from 'lucide-react';
import {
  generateClientLogoPresignedUploadUrl,
  uploadFileToS3,
  getCurrentClientLogo,
  getAllClientLogos,
  updateClientLogoStatus,
  activateClientLogo,
  deleteClientLogo,
  generateClientLogoDownloadUrl,
  updateClientLogoClientName,
  getLatestClientLogoGlobal,         
  getAllClientLogosFromAllAccounts,
  generateClientLogoDownloadUrlGlobal,
} from '../../../service/apiService';
import { useAuth } from '../../../context/AuthContext'; 

interface ClientLogoItem {
  id: string;
  file_name: string;
  file_type: string;
  file_size: string;
  status: string;
  is_current: string;
  uploaded_by: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  s3_key: string;
  s3_bucket: string;
  version: number;
  original_file_name?: string;
  uploaded_at?: string;
  client_name?: string;
}

interface UploadProgress {
  file: File;
  progress: number;
  status: 'uploading' | 'success' | 'error';
  error?: string;
  logoId?: string;
}
// Updated Modal component using Headless UI
interface DeleteModalProps {
  isOpen: boolean;
  fileName: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const DeleteModal: React.FC<DeleteModalProps> = ({ 
  isOpen, 
  fileName, 
  isDeleting, 
  onConfirm, 
  onCancel 
}) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !isDeleting && onCancel()}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                {/* Warning Icon */}
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                </div>
                
                {/* Title */}
                <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 text-center mb-2">
                  Delete Client Logo
                </Dialog.Title>
                
                {/* Content */}
                <div className="mb-6 text-center">
                  <p className="text-sm text-gray-500 mb-2">
                    Are you sure you want to delete
                  </p>
                  <p className="text-sm font-medium text-gray-900 mb-3">
                    "{fileName}"?
                  </p>
                  <p className="text-sm text-red-600 font-medium">
                    ⚠️ This action cannot be undone.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex space-x-3 justify-center">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 disabled:opacity-50"
                    onClick={onCancel}
                    disabled={isDeleting}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50"
                    onClick={onConfirm}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Deleting...
                      </div>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
const ClientLogoManagement: React.FC = () => {
  const [currentLogo, setCurrentLogo] = useState<ClientLogoItem | null>(null);
  const [allLogos, setAllLogos] = useState<ClientLogoItem[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteFileName, setConfirmDeleteFileName] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [clientName, setClientName] = useState<string>('');
  const [editingLogoId, setEditingLogoId] = useState<string | null>(null);
  const [editingClientName, setEditingClientName] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { isAuthenticated, isLoading: authLoading } = useAuth(); 

  // Memoize current logo to prevent unnecessary re-renders
  const memoizedCurrentLogo = useMemo(() => currentLogo, [currentLogo?.id, currentLogo?.file_name, currentLogo?.client_name]);
  // Optimized loadLogos function with useCallback
  const loadLogos = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      console.log('🔍 Loading logos from all accounts...');
      
      const [currentLogoResponse, allLogosResponse] = await Promise.allSettled([
        getLatestClientLogoGlobal(),           
        getAllClientLogosFromAllAccounts()     
      ]);
      
      // Handle responses and batch state updates
      let newCurrentLogo: ClientLogoItem | null = null;
      let newAllLogos: ClientLogoItem[] = [];
      
      if (currentLogoResponse.status === 'fulfilled' && currentLogoResponse.value) {
        newCurrentLogo = currentLogoResponse.value;
      }
      
      if (allLogosResponse.status === 'fulfilled' && allLogosResponse.value) {
        newAllLogos = allLogosResponse.value.data || [];
      }
      
      // Batch state updates to prevent multiple re-renders
      setCurrentLogo(newCurrentLogo);
      setAllLogos(newAllLogos);
      
    } catch (error) {
      console.error('❌ Error loading logos:', error);
      setError('Failed to load client logos');
      setCurrentLogo(null);
      setAllLogos([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Optimized handlers with useCallback
  const handleActivateLogo = useCallback(async (logoId: string) => {
    try {
      setError(null);
      await activateClientLogo(logoId);
      await loadLogos();
    } catch (error) {
      console.error('Error activating logo:', error);
      setError('Failed to activate logo');
    }
  }, [loadLogos]);

  const handleDownloadLogo = useCallback(async (logoId: string, fileName: string) => {
    try {
      setError(null);
      const downloadResponse = await generateClientLogoDownloadUrlGlobal(logoId);
      
      const link = document.createElement('a');
      link.href = downloadResponse.presigned_url;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading logo:', error);
      setError('Failed to download logo');
    }
  }, []);

  const handleDeleteLogo = useCallback((logoId: string, fileName: string) => {
    setConfirmDeleteId(logoId);
    setConfirmDeleteFileName(fileName);
    setShowDeleteModal(true);
  }, []);

  const confirmDeleteLogo = useCallback(async () => {
  if (!confirmDeleteId) {
    setError("Error: No logo selected for deletion");
    return;
  }

  setIsDeleting(true);
  try {
    setError(null);
    await deleteClientLogo(confirmDeleteId);
    await loadLogos();
    setConfirmDeleteId(null);
    setConfirmDeleteFileName('');
    setShowDeleteModal(false);
  } catch (error) {
    console.error('Error deleting logo:', error);
    
    // Check for access denied errors (403 or 404)
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as any;
      if (axiosError.response?.status === 404 || axiosError.response?.status === 403) {
        setError('You don\'t have access to delete this. Only creators can delete');
        setConfirmDeleteId(null);
        setConfirmDeleteFileName('');
        setShowDeleteModal(false);
        return;
      }
    }
    
    // Default error handling for other errors
    let errorMessage = 'Failed to delete logo';
    if (error instanceof Error) {
      errorMessage = `Failed to delete logo: ${error.message}`;
    }
    setError(errorMessage);
    setConfirmDeleteId(null);
    setConfirmDeleteFileName('');
    setShowDeleteModal(false);
  } finally {
    setIsDeleting(false);
  }
}, [confirmDeleteId, loadLogos]);

const handleCancelDelete = useCallback(() => {
  setConfirmDeleteId(null);
  setConfirmDeleteFileName('');
  setShowDeleteModal(false);
}, []);

  const handleEditClientName = useCallback((logoId: string, currentClientName: string) => {
    setEditingLogoId(logoId);
    setEditingClientName(currentClientName || '');
  }, []);

  const handleSaveClientName = useCallback(async (logoId: string) => {
  if (!editingClientName.trim()) {
    setError('Client name cannot be empty');
    return;
  }

  try {
    setError(null);
    await updateClientLogoClientName(logoId, editingClientName);
    await loadLogos();
    setEditingLogoId(null);
    setEditingClientName('');
  } catch (error) {
    console.error('Error updating client name:', error);
    
    // Check if it's a 403 Forbidden error
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as any;
      if (axiosError.response?.status === 403) {
        setError('You don\'t have access to modify others created logo name');
        return;
      }
    }
    
    // Default error message for other errors
    setError('Failed to update client name');
  }
}, [editingClientName, loadLogos]);

  const handleCancelEditClientName = useCallback(() => {
    setEditingLogoId(null);
    setEditingClientName('');
  }, []);

  const clearUploadProgress = useCallback(() => {
    setUploadProgress([]);
  }, []);

  // Load logos on component mount with cleanup
useEffect(() => {
  let isMounted = true;
  
  const loadData = async () => {
    // Wait for auth to complete, then load data
    if (isMounted && !authLoading && isAuthenticated) {
      await loadLogos();
    }
  };
  
  loadData();
  
  return () => {
    isMounted = false;
  };
}, [loadLogos, authLoading, isAuthenticated]);

  const validateFile = (file: File, clientName: string): { isValid: boolean; error?: string } => {
    const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    const maxSize = 10 * 1024 * 1024; 
    
    if (!validTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `${file.name} is not a valid image type. Please use PNG, JPG, JPEG, or SVG.`
      };
    }
    
    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `${file.name} is too large. Maximum file size is 10MB.`
      };
    }

    if (!clientName || !clientName.trim()) {
      return {
        isValid: false,
        error: 'Client name is required for logo upload.'
      };
    }
    
    return { isValid: true };
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (!clientName || !clientName.trim()) {
      setError('Please enter a client name before uploading.');
      return;
    }

    const validationResults = acceptedFiles.map(file => ({
      file,
      validation: validateFile(file, clientName)
    }));

    const invalidFiles = validationResults.filter(result => !result.validation.isValid);
    if (invalidFiles.length > 0) {
      setError(invalidFiles[0].validation.error || 'Invalid file');
      return;
    }

    const validFiles = validationResults.map(result => result.file);

    const initialProgress = validFiles.map(file => ({
      file,
      progress: 0,
      status: 'uploading' as const
    }));
    
    setUploadProgress(initialProgress);
    setError(null);

    for (let i = 0; i < validFiles.length; i++) {
      const file = validFiles[i];
      await uploadFile(file, i);
    }
  }, [clientName]);

  const uploadFile = async (file: File, index: number) => {
    try {
      console.log('Generating presigned URL for:', file.name, 'with client name:', clientName);
      const presignedResponse = await generateClientLogoPresignedUploadUrl(
        file.name,
        file.size.toString(),
        clientName
      );

      console.log('Presigned response:', presignedResponse);

      const metadata = {
        content_type: file.type,
        file_size: file.size.toString(),
        logo_id: presignedResponse.file_id,
        uploaded_by: 'user',
        created_by: 'user',
        updated_by: 'user'
      };

      await uploadFileToS3(
        presignedResponse.presigned_url,
        file,
        (progress) => {
          setUploadProgress(prev => prev.map((item, i) => 
            i === index ? { ...item, progress } : item
          ));
        },
        metadata
      );

      await updateClientLogoStatus(presignedResponse.file_id, 'active');

      setUploadProgress(prev => prev.map((item, i) => 
        i === index ? { 
          ...item, 
          progress: 100, 
          status: 'success',
          logoId: presignedResponse.file_id 
        } : item
      ));

      await loadLogos();
      setClientName('');

    } catch (error) {
      console.error('Upload error:', error);
      
      let errorMessage = 'Upload failed';
      if (error instanceof Error) {
        errorMessage = error.message;
        
        if (error.message.includes('Client name is required')) {
          errorMessage = 'Client name is required for logo upload.';
        } else if (error.message.includes('403')) {
          errorMessage = 'Upload failed: Access denied. Please check your permissions.';
        } else if (error.message.includes('404')) {
          errorMessage = 'Upload failed: Upload URL not found. Please try again.';
        } else if (error.message.includes('413')) {
          errorMessage = 'Upload failed: File too large.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Upload failed: Request timed out. Please try again.';
        }
      }
      
      setUploadProgress(prev => prev.map((item, i) => 
        i === index ? { 
          ...item, 
          status: 'error',
          error: errorMessage
        } : item
      ));
      
      setError(errorMessage);
    }
  };

  const formatFileSize = (bytes: string | number) => {
    const numBytes = typeof bytes === 'string' ? parseInt(bytes) : bytes;
    if (numBytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(numBytes) / Math.log(k));
    return parseFloat((numBytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'text-green-600';
      case 'processing':
        return 'text-yellow-600';
      case 'failed':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'processing':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'failed':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.svg']
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 bg-white rounded-lg">
        <div className="flex flex-col items-center space-y-3">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          <span className="text-gray-600">Loading client logos...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 min-h-screen">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Client Logo Management</h1>
              <p className="text-gray-600 mt-1">Manage your organization's brand assets</p>
            </div>
            <div className="flex items-center text-gray-500">
              <ImageIcon className="w-6 h-6" />
            </div>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="text-red-600 hover:text-red-800 underline text-sm mt-1"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Current Active Logo */}
        {memoizedCurrentLogo && (
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Current Active Logo
            </h2>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-white rounded-lg border">
                  <ImageIcon className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{memoizedCurrentLogo.file_name}</h3>
                  <p className="text-sm text-purple-600 font-medium">
                    { /* Client name editing section */}
                    <div className="text-sm text-gray-600 mb-1">
                     {editingLogoId === memoizedCurrentLogo.id ? (
                       <div className="flex items-center space-x-2 max-w-sm">
                         <input
                           type="text"
                           value={editingClientName}
                           onChange={(e) => setEditingClientName(e.target.value)}
                           placeholder="Enter client name..."
                           className="text-sm bg-white text-gray-900 border border-gray-300 rounded px-2 py-1 flex-1 min-w-0"
                          />
                         <button
                           onClick={() => handleSaveClientName(memoizedCurrentLogo.id)}
                           className="text-green-600 hover:text-green-800 p-1"
                           title="Save"
                          >
                        <Check className="w-4 h-4" />
                      </button>
                     <button
                       onClick={handleCancelEditClientName}
                       className="text-red-600 hover:text-red-800 p-1"
                       title="Cancel"
                      >
                    <X className="w-4 h-4" />
                   </button>
                 </div>
                ) : (
                <div className="flex items-center space-x-1">
                  <span className="text-sm font-medium text-purple-600">
                    Client: {memoizedCurrentLogo.client_name || 'Not specified'}
                 </span>
                <button
                  onClick={() => handleEditClientName(memoizedCurrentLogo.id, memoizedCurrentLogo.client_name || '')}
                  className="text-purple-600 hover:text-purple-800 p-1"
                  title="Edit client name"
                >
              <Pencil className="w-4 h-4" />
            </button>
            </div>
             )}
           </div>         
                </p>
                  <p className="text-sm text-blue-600 font-medium">
                    Uploaded by: {memoizedCurrentLogo.uploaded_by}
                  </p>
                  <p className="text-sm text-gray-500">
                    Version {memoizedCurrentLogo.version} • {formatFileSize(memoizedCurrentLogo.file_size)} • 
                    Status: <span className={`font-medium ${getStatusColor(memoizedCurrentLogo.status)}`}>
                      {memoizedCurrentLogo.status}
                    </span>
                  </p>
                  <p className="text-xs text-gray-400">
                    Uploaded {formatDate(memoizedCurrentLogo.created_at)}
                  </p>
                </div>
              </div>
                            <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleDownloadLogo(memoizedCurrentLogo.id, memoizedCurrentLogo.file_name)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDeleteLogo(memoizedCurrentLogo.id, memoizedCurrentLogo.file_name)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  title="Delete Active Logo"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Upload Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-purple-600" />
            Upload New Logo
          </h2>
          
          <div className="space-y-4">
            {/* Client Name Input */}
            <div>
              <label htmlFor="clientName" className="block text-sm font-medium text-gray-700 mb-2">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="clientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client name"
                className={`w-full px-3 py-2 bg-white text-gray-900 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors ${
                  !clientName.trim() && error?.includes('Client name')
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300'
                }`}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                This will be associated with the uploaded logo
              </p>
            </div>

            {/* Upload Area */}
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                !clientName.trim() 
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50' 
                  : isDragActive 
                    ? 'border-purple-400 bg-purple-50 cursor-pointer' 
                    : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50 cursor-pointer'
              }`}
            >
              <input {...getInputProps()} disabled={!clientName.trim()} />
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {!clientName.trim() 
                  ? 'Enter client name first to enable upload' 
                  : isDragActive 
                    ? 'Drop files here' 
                    : 'Drag & drop logo files here'
                }
              </p>
              {clientName.trim() && (
                <>
                  <p className="text-gray-500 mb-4">or click to browse</p>
                  <p className="text-sm text-gray-400">
                    Supports PNG, JPG, JPEG, SVG up to 10MB
                  </p>
                </>
              )}
            </div>

            {/* Upload Progress */}
            {uploadProgress.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="font-medium text-gray-900">Upload Progress</h4>
                  <button
                    onClick={clearUploadProgress}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Clear
                  </button>
                </div>
                {uploadProgress.map((upload, index) => (
                  <div key={index} className="bg-gray-50 border rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-gray-900 truncate pr-2">{upload.file.name}</span>
                      <div className="flex items-center space-x-2">
                        {upload.status === 'uploading' && (
                          <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                        )}
                        {upload.status === 'success' && (
                          <Check className="w-4 h-4 text-green-600" />
                        )}
                        {upload.status === 'error' && (
                          <X className="w-4 h-4 text-red-600" />
                        )}
                        <span className="text-sm text-gray-500 min-w-[3rem]">
                          {upload.progress.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          upload.status === 'error' 
                            ? 'bg-red-500' 
                            : upload.status === 'success'
                            ? 'bg-green-500'
                            : 'bg-purple-500'
                        }`}
                        style={{ width: `${upload.progress}%` }}
                      />
                    </div>
                    {upload.error && (
                      <p className="text-sm text-red-600 mt-1">{upload.error}</p>
                    )}
                    {upload.status === 'success' && (
                      <p className="text-sm text-green-600 mt-1">Upload completed successfully!</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* All Logos List */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Eye className="w-5 h-5 text-gray-600" />
            All Logos ({allLogos.length})
          </h2>
          
          {allLogos.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <ImageIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-lg font-medium">No logos uploaded yet</p>
              <p className="text-sm">Upload your first logo using the area above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allLogos.map((logo) => (
                <div 
                  key={logo.id} 
                  className={`border rounded-lg p-4 transition-colors ${
                    logo.is_current === 'true' 
                      ? 'border-purple-200 bg-purple-50' 
                      : 'border-gray-200 bg-white hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center min-w-0 flex-1">
                      <div className="p-2 bg-white border rounded-lg mr-3">
                        <ImageIcon className="w-6 h-6 text-gray-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center">
                          <p className="font-medium text-gray-900 truncate">{logo.file_name}</p>
                          {logo.is_current === 'true' && (
                            <Star className="w-4 h-4 ml-2 text-purple-600 fill-current flex-shrink-0" />
                          )}
                        </div>
                        
                        {/* Client name editing section */}
                        <div className="text-sm text-gray-600 mb-1">
                          {editingLogoId === logo.id ? (
                            <div className="flex items-center space-x-2 max-w-sm">
                              <input
                                type="text"
                                value={editingClientName}
                                onChange={(e) => setEditingClientName(e.target.value)}
                                placeholder="Enter client name..."
                                className="text-xs bg-white text-gray-900 border border-gray-300 rounded px-2 py-1 flex-1 min-w-0"
                              />
                              <button
                                onClick={() => handleSaveClientName(logo.id)}
                                className="text-green-600 hover:text-green-800 p-1"
                                title="Save"
                              >
                                <Check className="w-3 h-3" />
                              </button>
                              <button
                                onClick={handleCancelEditClientName}
                                className="text-red-600 hover:text-red-800 p-1"
                                title="Cancel"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <span className="text-xs font-medium text-purple-600">
                                Client: {logo.client_name || 'Not specified'}
                              </span>
                              <button
                                onClick={() => handleEditClientName(logo.id, logo.client_name || '')}
                                className="text-purple-600 hover:text-purple-800 p-1"
                                title="Edit client name"
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2 text-sm">
                          <span className={`px-2 py-1 rounded-full border text-xs font-medium ${getStatusBadge(logo.status)}`}>
                            {logo.status}
                          </span>
                          <span className="text-gray-500">Version {logo.version}</span>
                          <span className="text-gray-500">{formatFileSize(logo.file_size)}</span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Uploaded {formatDate(logo.created_at)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2 ml-4"> 
                      <button
                        onClick={() => handleDownloadLogo(logo.id, logo.file_name)}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </button>
                      
                      <button
                        onClick={() => handleDeleteLogo(logo.id, logo.file_name)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                    <Trash2 className="w-4 h-4" />
                  </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
            <Info className="w-4 h-4 text-gray-600" />
            Instructions
          </h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <strong>Client Name:</strong> Required for all logo uploads</li>
            <li>• <strong>Supported formats:</strong> PNG, JPG, JPEG, SVG</li>
            <li>• <strong>Maximum file size:</strong> 10MB</li>
            <li>• <strong>Recommended dimensions:</strong> 200x200px or higher for best quality</li>
            <li>• <strong>Active logo:</strong> Only one logo can be active at a time</li>
            <li>• <strong>Auto-activation:</strong> Uploading a new logo will automatically set it as active</li>
            <li>• <strong>Versioning:</strong> Each upload creates a new version while preserving previous ones</li>
            <li>• <strong>Client Name Editing:</strong> You can update client names after upload using the edit icon</li>
          </ul>
        </div>
         {/* Delete Confirmation Modal */}
        <DeleteModal
          isOpen={showDeleteModal}
          fileName={confirmDeleteFileName}
          isDeleting={isDeleting}
          onConfirm={confirmDeleteLogo}
          onCancel={handleCancelDelete}
        />
      </div>
    </div>
  );
};

export default ClientLogoManagement;